import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Clock, Wrench, CheckCircle, Users, Shield, Award, Truck, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import siteConfig from '../config/siteConfig';
import QuoteCalculator from '../components/QuoteCalculator';
import { AnimatedSection } from '../hooks/useScrollAnimation';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const InstallationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subject: `Demande devis installation - ${formData.city || 'Non spécifié'}`
        })
      });

      if (response.ok) {
        console.log('Success:', 'Demande envoyée ! Nous vous contacterons sous 24h.');
        setFormData({ name: '', email: '', phone: '', city: '', message: '' });
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      console.log('Error:', "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleQuoteRequest = (quoteData) => {
    const motionLabels = { basic: 'MotionProtect', cam: 'MotionCam HDR' };
    const equipmentLabels = {
      hub: 'Centrale Hub 2 4G',
      motionDetectors: motionLabels[quoteData.motionType],
      doorSensors: 'Détecteurs ouverture',
      indoorSiren: 'Sirène intérieure',
      outdoorSiren: 'Sirène extérieure',
      smokeDetector: 'Détecteur fumée',
      keypad: 'Clavier',
      remotes: 'Télécommandes',
    };

    const tvaLabel = quoteData.confirmed2Years ? '10%' : '20%';

    let message = `=== SIMULATION DEVIS ===\n`;
    message += `Ville: ${quoteData.city} (${quoteData.distance} km)\n`;
    message += `Logement: ${quoteData.propertyType === 'house' ? 'Maison' : 'Appartement'}\n`;
    message += `TVA: ${tvaLabel}${quoteData.confirmed2Years ? ' (logement > 2 ans)' : ''}\n\n`;
    message += `ÉQUIPEMENTS:\n`;
    
    Object.entries(quoteData.equipment).forEach(([key, qty]) => {
      if (qty > 0 && equipmentLabels[key]) {
        message += `• ${equipmentLabels[key]}: ${qty}\n`;
      }
    });

    if (quoteData.options.wantsCameras) {
      message += `• Caméras: Sur devis\n`;
    }

    message += `\nSERVICES:\n`;
    if (quoteData.options.installation) {
      message += `• Installation: ${quoteData.installationPrice}€ TTC\n`;
    }
    if (quoteData.options.programming) {
      message += `• Programmation: 150€ TTC\n`;
    }
    if (quoteData.options.subscription4G) {
      const type = quoteData.options.subscription4GType === 'annual' ? '99€ TTC/an' : '8,90€ TTC/mois';
      message += `• Forfait 4G: ${type}\n`;
    }

    message += `\n--- RÉCAPITULATIF ---\n`;
    message += `Total HT: ${quoteData.totals.totalHT.toFixed(2)}€\n`;
    message += `TVA ${tvaLabel}: ${quoteData.totals.tva.toFixed(2)}€\n`;
    message += `💰 TOTAL TTC: ${quoteData.totals.totalTTC.toFixed(2)}€`;
    if (quoteData.options.wantsCameras) {
      message += ` + caméras`;
    }
    if (quoteData.totals.monthlyTTC > 0) {
      message += `\n+ ${quoteData.totals.monthlyTTC.toFixed(2)}€/mois (4G)`;
    }

    setFormData(prev => ({ 
      ...prev, 
      city: quoteData.city,
      message 
    }));

    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
    console.log('Success:', 'Simulation pré-remplie ! Complétez vos coordonnées.');
  };

  const stats = [
    { icon: Users, value: '500+', label: 'Installations' },
    { icon: Shield, value: '100%', label: 'Satisfaction' },
    { icon: Clock, value: '24-48h', label: 'Réponse' },
    { icon: Award, value: '5 ans', label: 'Expérience' },
  ];

  const steps = [
    { num: '01', title: 'Simulation', desc: 'Estimez votre projet en ligne' },
    { num: '02', title: 'Contact', desc: 'Nous vous rappelons sous 24h' },
    { num: '03', title: 'Visite', desc: 'Étude technique gratuite' },
    { num: '04', title: 'Installation', desc: 'Pose par technicien certifié' },
    { num: '05', title: 'Formation', desc: 'Prise en main de votre système' },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="installation-page">
      {/* SEO Meta Tags for Installation Page */}
      <Helmet>
        <title>Installation Alarme Maison Montpellier | Alarme Sans Fil & Caméra - FGS</title>
        <meta name="description" content="Installation alarme maison et caméra sans fil à Montpellier. Alarme sans fil Ajax Systems, vidéosurveillance, télésurveillance. Devis gratuit, TVA 10%, garantie 4 ans. Intervention Hérault, Gard, Aude." />
        <meta name="keywords" content="installation alarme montpellier, alarme maison, alarme sans fil, caméra sans fil, vidéosurveillance montpellier, télésurveillance, alarme ajax systems, installateur alarme 34, alarme hérault, alarme gard, alarme aude, camera surveillance montpellier, système alarme maison" />
        <link rel="canonical" href="https://maprotection-ia.fr/installation" />
        
        {/* Open Graph for Installation Page */}
        <meta property="og:title" content="Installation Alarme Montpellier | FGS - Expert Ajax Systems" />
        <meta property="og:description" content="Installateur alarme Ajax Systems à Montpellier. Intervention dans tout le Sud: Hérault, Gard, Aude. Devis gratuit, TVA 10%." />
        <meta property="og:url" content="https://maprotection-ia.fr/installation" />
        <meta property="og:image" content="https://maprotection-ia.fr/og-image-fgs.png" />
        
        {/* Structured Data - LocalBusiness with Service */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://maprotection-ia.fr/#business",
            "name": "FGS Alarme Montpellier - MaProtection-IA",
            "alternateName": "FGS Installateur Ajax Systems Montpellier",
            "description": "Installation professionnelle d'alarme sans fil Ajax Systems à Montpellier et région Sud. Technicien certifié, garantie 4 ans, TVA réduite 10%.",
            "url": "https://maprotection-ia.fr",
            "telephone": "+33745040929",
            "email": "contact@maprotection-ia.fr",
            "image": "https://maprotection-ia.fr/og-image-fgs.png",
            "priceRange": "€€",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6 Bis Boulevard Berthelot, Bureau 3",
              "addressLocality": "Montpellier",
              "postalCode": "34000",
              "addressRegion": "Hérault",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "43.6047",
              "longitude": "3.8807"
            },
            "areaServed": [
              {"@type": "City", "name": "Montpellier"},
              {"@type": "City", "name": "Béziers"},
              {"@type": "City", "name": "Sète"},
              {"@type": "City", "name": "Nîmes"},
              {"@type": "City", "name": "Narbonne"},
              {"@type": "City", "name": "Perpignan"},
              {"@type": "City", "name": "Carcassonne"},
              {"@type": "City", "name": "Marseille"},
              {"@type": "State", "name": "Hérault"},
              {"@type": "State", "name": "Gard"},
              {"@type": "State", "name": "Aude"},
              {"@type": "State", "name": "Bouches-du-Rhône"}
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Services d'installation alarme",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Installation Alarme Ajax Systems",
                    "description": "Installation professionnelle système alarme sans fil"
                  },
                  "price": "350",
                  "priceCurrency": "EUR"
                }
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "147",
              "bestRating": "5",
              "worstRating": "1"
            }
          }
        `}</script>
        
        {/* FAQ Schema for SEO */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Combien coûte une installation d'alarme à Montpellier ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "L'installation d'une alarme Ajax Systems à Montpellier démarre à 350€ TTC. Le prix dépend de la superficie et du nombre de détecteurs. Bénéficiez de la TVA réduite à 10% pour les logements de plus de 2 ans."
                }
              },
              {
                "@type": "Question",
                "name": "Dans quelles villes intervenez-vous autour de Montpellier ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nous intervenons dans un rayon de 200km autour de Montpellier : Hérault (34), Gard (30), Aude (11), Bouches-du-Rhône (13), Var (83), Pyrénées-Orientales (66). Villes principales : Béziers, Sète, Nîmes, Narbonne, Perpignan, Carcassonne, Marseille, Aix-en-Provence."
                }
              },
              {
                "@type": "Question",
                "name": "Quelle garantie pour l'installation d'alarme ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "FGS offre une garantie de 4 ans sur tous les équipements Ajax Systems et 2 ans sur la pose. Intervention sous 48h en cas de problème."
                }
              }
            ]
          }
        `}</script>
      </Helmet>
      
      {/* Hero */}
      <section className="relative py-12 overflow-hidden bg-black">
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="text-center mb-8">
            <p className="text-emerald-400 text-sm font-medium tracking-wide uppercase mb-4">Installateur certifié Ajax Systems</p>
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
              Installation Alarme <span className="text-emerald-400">Montpellier</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto font-light">
              <strong className="text-white/70">FGS, votre installateur alarme Ajax Systems</strong> à Montpellier 
              et dans tout le <span className="text-emerald-400">Sud de la France</span> (rayon 200 km)
            </p>
            
            {/* Trust badges for SEO */}
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs text-white/50">
                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" /> 4.9/5 (147 avis)
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs text-white/50">
                <Shield className="w-3 h-3 text-emerald-400" /> Garantie 4 ans
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs text-white/50">
                <CheckCircle className="w-3 h-3 text-emerald-400" /> TVA 10%
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-2 md:gap-4 max-w-2xl mx-auto mb-8">
            {stats.map((stat, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl p-3 text-center">
                <stat.icon className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <div className="text-lg md:text-xl font-semibold text-white">{stat.value}</div>
                <div className="text-[10px] text-white/50 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TVA Banner */}
      <div className="relative py-2.5 bg-emerald-500/5 border-y border-fgs-primary/20 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-8 mx-4">
              <span className="flex items-center gap-2">
                <span className="text-emerald-400 font-medium text-sm">TVA 10%</span>
                <span className="text-white/60 text-sm">pour les logements de plus de 2 ans</span>
              </span>
              <span className="text-white/10">•</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <AnimatedSection>
      <section className="py-12 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Quote Calculator - Takes more space */}
            <div className="lg:col-span-3">
              <QuoteCalculator onRequestQuote={handleQuoteRequest} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* Process Steps */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Comment ça marche ?</h3>
                <div className="space-y-4">
                  {steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-400 text-xs font-bold">{step.num}</span>
                      </div>
                      <div>
                        <h4 className="text-white font-medium text-sm">{step.title}</h4>
                        <p className="text-white/50 text-xs">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Services inclus */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-emerald-400" />
                  Inclus dans l'installation
                </h3>
                <ul className="space-y-2">
                  {[
                    'Visite technique gratuite',
                    'Pose par technicien certifié Ajax',
                    'Configuration complète',
                    'Paramétrage application',
                    'Formation utilisation',
                    'Garantie 2 ans pose',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-fgs-success flex-shrink-0" />
                      <span className="text-white/50">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Zone info */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-emerald-400" />
                  Zone d'intervention alarme
                </h3>
                <p className="text-white/50 text-sm mb-3">
                  <strong className="text-white">Installateur alarme Montpellier</strong> et région Sud dans un rayon de 200 km :
                </p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {['Hérault (34)', 'Gard (30)', 'Aude (11)', 'Bouches-du-Rhône', 'Var (83)', 'Pyrénées-Or.'].map((dept) => (
                    <span key={dept} className="px-2 py-1 bg-black/40 border border-white/5 rounded text-xs text-white/50">
                      {dept}
                    </span>
                  ))}
                </div>
                <p className="text-white/50 text-xs">
                  <span className="text-emerald-400">Villes :</span> Béziers, Sète, Nîmes, Narbonne, Perpignan, Carcassonne, Marseille, Aix-en-Provence, Toulon
                </p>
              </div>

              {/* Contact */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Contact direct</h3>
                <a 
                  href={`tel:${siteConfig.contact.phoneFormatted}`}
                  className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/10 transition-colors mb-3"
                >
                  <Phone className="w-5 h-5 text-emerald-400" />
                  <span className="text-white font-semibold">{siteConfig.contact.phone}</span>
                </a>
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Lun-Ven 8h-18h • Sam 9h-12h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-12 bg-gradient-to-b from-transparent via-fgs-surface/30 to-transparent">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-white mb-2">
                Devis Alarme Montpellier Gratuit
              </h2>
              <p className="text-white/50 text-sm">
                Recevez votre devis personnalisé sous 24h pour votre installation alarme
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/50 text-xs uppercase tracking-wider">Nom *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Votre nom"
                    className="bg-black/40 border-white/5 text-white placeholder:text-white/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/50 text-xs uppercase tracking-wider">Ville *</Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="Votre ville"
                    className="bg-black/40 border-white/5 text-white placeholder:text-white/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/50 text-xs uppercase tracking-wider">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="votre@email.com"
                    className="bg-black/40 border-white/5 text-white placeholder:text-white/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/50 text-xs uppercase tracking-wider">Téléphone *</Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="06 12 34 56 78"
                    className="bg-black/40 border-white/5 text-white placeholder:text-white/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/50 text-xs uppercase tracking-wider">Détails du projet</Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  placeholder="Décrivez votre projet ou utilisez le simulateur ci-dessus..."
                  className="bg-black/40 border-white/5 text-white placeholder:text-white/50 resize-none font-mono text-sm"
                />
              </div>

              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-500-hover h-12 font-semibold"
              >
                {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </Button>
            </form>
          </div>
        </div>
      </section>
      </AnimatedSection>

      {/* FAQ Section for SEO */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-semibold text-white text-center mb-8">
            Questions fréquentes - Alarme Montpellier
          </h2>
          <div className="space-y-4">
            <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-white mb-2">
                Combien coûte une installation d'alarme à Montpellier ?
              </h3>
              <p className="text-white/50 text-sm">
                L'installation d'une alarme Ajax Systems à Montpellier démarre à <strong className="text-white">350€ TTC</strong>. 
                Le prix final dépend de la superficie de votre logement et du nombre de détecteurs nécessaires. 
                Pour les logements de plus de 2 ans, bénéficiez de la <strong className="text-emerald-400">TVA réduite à 10%</strong>.
              </p>
            </div>
            <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-white mb-2">
                Dans quelles villes du Sud intervenez-vous ?
              </h3>
              <p className="text-white/50 text-sm">
                FGS intervient dans un rayon de 200 km autour de Montpellier : <strong className="text-white">Hérault (34)</strong>, Gard (30), Aude (11), 
                Bouches-du-Rhône (13), Var (83), Pyrénées-Orientales (66). Villes principales : Béziers, Sète, Nîmes, Narbonne, 
                Perpignan, Carcassonne, Marseille, Aix-en-Provence, Toulon.
              </p>
            </div>
            <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-white mb-2">
                Quelle garantie pour mon alarme Ajax Systems ?
              </h3>
              <p className="text-white/50 text-sm">
                FGS offre une <strong className="text-emerald-400">garantie de 4 ans</strong> sur tous les équipements Ajax Systems 
                et <strong className="text-white">2 ans sur la pose</strong>. En cas de problème, nous intervenons sous 48h maximum.
              </p>
            </div>
            <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-white mb-2">
                Pourquoi choisir Ajax Systems pour mon alarme ?
              </h3>
              <p className="text-white/50 text-sm">
                Ajax Systems est la <strong className="text-white">référence mondiale</strong> des alarmes sans fil professionnelles. 
                Connexion 4G + Wi-Fi, détection IA (photo vérifée), autonomie jusqu'à 7 ans, 
                application mobile intuitive. N°1 en Europe avec plus de 3 millions d'utilisateurs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="rounded-2xl overflow-hidden border border-white/5 h-[300px]">
            <iframe
              src={siteConfig.address.mapsEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
              allowFullScreen=""
              loading="lazy"
              title="FGS Alarme Montpellier Montpellier"
            />
          </div>
          <p className="text-center text-white/50 text-sm mt-4">
            <MapPin className="w-4 h-4 inline mr-1" />
            {siteConfig.address.full}
          </p>
        </div>
      </section>
    </div>
  );
};

export default InstallationPage;
